/**
 * Countdown Timer
 * Initializes all countdown timers on the page using PST timezone.
 * Each timer element should have:
 * - data-countdown-timer attribute
 * - data-countdown-end-date attribute with format: "YYYY, M, D, H, M, S" in PST
 */
(function () {
  'use strict';

  const SECONDS_PER_MINUTE = 60;
  const MINUTES_PER_HOUR = 60;
  const HOURS_PER_DAY = 24;
  const MILLISECONDS_PER_SECOND = 1000;
  const PST_OFFSET_HOURS = 8; // PST is UTC-8

  /**
   * Pad a number with leading zeros
   * @param {number} value
   * @returns {string}
   */
  const pad = (value) => String(Math.max(0, value)).padStart(2, '0');

  /**
   * Parse the end date string (in PST) into a UTC timestamp
   * @param {string} dateString - Format: "YYYY, M, D, H, M, S" in PST
   * @returns {number|null} - UTC timestamp in milliseconds or null if invalid
   */
  const parseEndDate = (dateString) => {
    if (!dateString) return null;

    const parts = dateString.split(',').map((part) => parseInt(part.trim(), 10));
    if (parts.length < 3 || parts.some((part) => Number.isNaN(part))) return null;

    const [year, month, day, hour = 0, minute = 0, second = 0] = parts;

    // Create UTC timestamp, then add PST offset to convert PST input to UTC
    // PST is UTC-8, so we add 8 hours to get the equivalent UTC time
    const pstAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
    return pstAsUtc + PST_OFFSET_HOURS * MINUTES_PER_HOUR * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;
  };

  /**
   * Initialize a single countdown timer
   * @param {HTMLElement} timerElement
   */
  const initTimer = (timerElement) => {
    const endDateString = timerElement.dataset.countdownEndDate;
    const targetUTC = parseEndDate(endDateString);

    if (!targetUTC) {
      timerElement.hidden = true;
      return;
    }

    const daysEl = timerElement.querySelector('[data-days]');
    const hoursEl = timerElement.querySelector('[data-hours]');
    const minutesEl = timerElement.querySelector('[data-minutes]');
    const secondsEl = timerElement.querySelector('[data-seconds]');

    let timerId = null;

    const updateTimer = () => {
      const nowUTC = Date.now();
      const remaining = targetUTC - nowUTC;

      // Countdown expired
      if (remaining <= 0) {
        timerElement.hidden = true;
        if (timerId) {
          window.clearInterval(timerId);
          timerId = null;
        }
        return;
      }

      // Show the timer
      timerElement.hidden = false;

      // Calculate time units
      const totalSeconds = Math.floor(remaining / MILLISECONDS_PER_SECOND);
      const days = Math.floor(totalSeconds / (HOURS_PER_DAY * MINUTES_PER_HOUR * SECONDS_PER_MINUTE));
      const hours = Math.floor(
        (totalSeconds % (HOURS_PER_DAY * MINUTES_PER_HOUR * SECONDS_PER_MINUTE)) /
          (MINUTES_PER_HOUR * SECONDS_PER_MINUTE)
      );
      const minutes = Math.floor((totalSeconds % (MINUTES_PER_HOUR * SECONDS_PER_MINUTE)) / SECONDS_PER_MINUTE);
      const seconds = totalSeconds % SECONDS_PER_MINUTE;

      // Update DOM
      if (daysEl) daysEl.textContent = pad(days);
      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minutesEl) minutesEl.textContent = pad(minutes);
      if (secondsEl) secondsEl.textContent = pad(seconds);
    };

    // Initial render
    updateTimer();

    // Start interval
    timerId = window.setInterval(updateTimer, 1000);
  };

  /**
   * Initialize all countdown timers on the page
   */
  const initAllTimers = () => {
    const timers = document.querySelectorAll('[data-countdown-timer]');
    timers.forEach(initTimer);
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllTimers, { once: true });
  } else {
    initAllTimers();
  }
})();

